import React from 'react';
import { Checkbox, CheckboxWithLabel } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import Link from 'next/link';
import { useRouter } from 'next/router';

import * as styles from './CheckboxGroup.styles';

const doNothing = () => undefined;

type CheckboxData = {
  checked?: boolean;
  crawlable?: boolean;
  disabled?: boolean;
  href?: string;
  labelText: string;
  secondaryLabelText?: string;
  value: string;
};

export interface CheckboxGroupProps {
  accessibleLabel: string;
  accessibleLabelElementId?: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  checkboxData: CheckboxData[];
  hideLabel?: boolean;
  name: string;
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
}

interface CrawlableLinkProps {
  accessibleName: string;
  active: boolean;
  href: string;
  isNavigationOnly: boolean;
  testId: string;
}

const CrawlableLink = ({
  accessibleName,
  active,
  href,
  isNavigationOnly,
  testId,
}: CrawlableLinkProps) => (
  <Link
    aria-current={isNavigationOnly && active ? 'true' : undefined}
    aria-hidden={isNavigationOnly ? undefined : 'true'}
    css={[styles.linkUnderlay, !isNavigationOnly && styles.crawlOnlyUnderlay]}
    data-testid={testId}
    href={href}
    prefetch={false}
    scroll={false}
    shallow
    tabIndex={isNavigationOnly ? undefined : -1}
  >
    <span css={visuallyHidden}>{accessibleName}</span>
  </Link>
);

interface NonCrawlableButtonProps {
  accessibleName: string;
  active: boolean;
  href: string;
  testId: string;
}

// `useRouter` is scoped here so the parent doesn't depend on a Next.js
// RouterContext unless this branch actually renders.
const NonCrawlableButton = ({ accessibleName, active, href, testId }: NonCrawlableButtonProps) => {
  const router = useRouter();
  return (
    <button
      aria-current={active ? 'true' : undefined}
      css={styles.linkUnderlay}
      data-href={href}
      data-testid={testId}
      onClick={() => router.push(href, undefined, { shallow: true, scroll: false })}
      type="button"
    >
      <span css={visuallyHidden}>{accessibleName}</span>
    </button>
  );
};

export const CheckboxGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  onChange,
  checkboxData,
  hideLabel,
}: CheckboxGroupProps) => {
  return (
    <div aria-labelledby={accessibleLabelElementId} role="group">
      <span css={hideLabel ? visuallyHidden : undefined} id={accessibleLabelElementId}>
        <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
      </span>
      {checkboxData.map(
        ({
          checked,
          crawlable,
          disabled,
          href,
          labelText,
          secondaryLabelText,
          value: checkboxValue,
        }) => {
          const hasNavigableHref = Boolean(href && !disabled);
          const isNavigationOnly = hasNavigableHref && !onChange;
          const checkboxInputId = `${name}-${checkboxValue}`;
          const accessibleName = `${labelText}${
            secondaryLabelText ? ` ${secondaryLabelText}` : ''
          }`;

          let navigationElement: React.ReactNode = null;
          if (hasNavigableHref && crawlable) {
            navigationElement = (
              <CrawlableLink
                accessibleName={accessibleName}
                active={Boolean(checked)}
                href={href!}
                isNavigationOnly={isNavigationOnly}
                testId={`${checkboxValue}-link`}
              />
            );
          } else if (isNavigationOnly) {
            navigationElement = (
              <NonCrawlableButton
                accessibleName={accessibleName}
                active={Boolean(checked)}
                href={href!}
                testId={`${checkboxValue}-button`}
              />
            );
          }

          const linkedCheckbox = (
            <>
              {navigationElement}
              <span css={styles.checkboxLabelForeground}>
                <Checkbox
                  aria-hidden={isNavigationOnly ? true : undefined}
                  checked={checked}
                  disabled={disabled}
                  id={checkboxInputId}
                  name={name}
                  onChange={onChange ?? doNothing}
                  tabIndex={isNavigationOnly ? -1 : undefined}
                  value={checkboxValue}
                />
                <span
                  css={styles.checkboxLabelTextSpan}
                  dangerouslySetInnerHTML={{ __html: labelText }}
                />
              </span>
            </>
          );

          const rowCss = [
            styles.checkboxLabelBase,
            disabled && styles.checkboxLabelDisabledState,
            styles.checkboxWrapper,
          ];
          let checkboxRow: React.ReactNode;
          if (isNavigationOnly) {
            checkboxRow = <span css={rowCss}>{linkedCheckbox}</span>;
          } else if (hasNavigableHref) {
            checkboxRow = (
              <label css={rowCss} htmlFor={checkboxInputId}>
                {linkedCheckbox}
              </label>
            );
          } else {
            checkboxRow = (
              <CheckboxWithLabel
                checked={checked}
                css={styles.checkboxWrapper}
                disabled={disabled}
                label={labelText}
                name={name}
                onChange={onChange ?? doNothing}
                value={checkboxValue}
              />
            );
          }

          return (
            <div
              css={styles.checkboxWrapperContainer}
              data-testid={checkboxValue}
              key={checkboxValue}
            >
              {checkboxRow}
              {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
            </div>
          );
        },
      )}
    </div>
  );
};
